import {
  type IAdminAuthLogEntry,
  type IAdminProfile,
  type IAdminSessionEntry,
} from '@mahawi/eshop-common';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import {
  type ELoginStatus,
  type IChangePasswordResponsePayload,
  type IUserState,
} from './types';

export const preloadedState: IUserState = {
  ...asyncStateInitial,
  isLoggedIn: false,
  loginStatus: null,
  passwordChanged: false,
  authLog: [],
  authLogInProcess: false,
  sessions: [],
  sessionsInProcess: false,
  profile: null,
  profileInProcess: false,
  email: undefined,
  uuid: undefined,
  username: undefined,
};

const userSlice = createSlice({
  name: 'User',
  initialState: preloadedState,
  reducers: {
    login: {
      reducer(state) {
        startLoading(state);
        state.isLoggedIn = false;
        state.loginStatus = null;
      },
      prepare(email: string, password: string) {
        return { payload: { email, password } };
      },
    },
    loginResponse: {
      reducer(
        state,
        action: { payload: { email: string; status: ELoginStatus } },
      ) {
        state.email = action.payload.email;
        state.loginStatus = action.payload.status;
        finishLoading(state);
      },
      prepare(email: string, status: ELoginStatus) {
        return { payload: { email, status } };
      },
    },
    loginReset(state) {
      state.loginStatus = null;
      state.email = undefined;
    },
    changePassword: {
      reducer(state) {
        startLoading(state);
        state.passwordChanged = false;
      },
      prepare(currentPassword: string, newPassword: string) {
        return { payload: { currentPassword, newPassword } };
      },
    },
    changePasswordResponse: {
      reducer(state, action: { payload: IChangePasswordResponsePayload }) {
        state.passwordChanged = action.payload.ok;
        finishLoading(state);
      },
      prepare(ok: boolean) {
        return { payload: { ok } };
      },
    },
    usernameChange: {
      reducer(state) {
        startLoading(state);
      },
      prepare(username: string | null) {
        return { payload: { username } };
      },
    },
    usernameChangeResponse: {
      reducer(state, action: { payload: { username: string | null } }) {
        state.username = action.payload.username;
        finishLoading(state);
      },
      prepare(username: string | null) {
        return { payload: { username } };
      },
    },
    profileLoad: {
      reducer(state, action: { payload: { accountUUID: string } }) {
        if (state.profile?.uuid !== action.payload.accountUUID) {
          state.profile = null;
        }

        state.profileInProcess = true;
      },
      prepare(accountUUID: string) {
        return { payload: { accountUUID } };
      },
    },
    profileLoadResponse: {
      reducer(state, action: { payload: { profile: IAdminProfile | null } }) {
        state.profile = action.payload.profile;
        state.profileInProcess = false;
      },
      prepare(profile: IAdminProfile | null) {
        return { payload: { profile } };
      },
    },
    authLogLoad(state) {
      state.authLogInProcess = true;
    },
    authLogLoadResponse: {
      reducer(state, action: { payload: { authLog: IAdminAuthLogEntry[] } }) {
        state.authLog = action.payload.authLog;
        state.authLogInProcess = false;
      },
      prepare(authLog: IAdminAuthLogEntry[]) {
        return { payload: { authLog } };
      },
    },
    sessionsLoad(state) {
      state.sessionsInProcess = true;
    },
    sessionRevoke: {
      reducer(state) {
        state.sessionsInProcess = true;
      },
      prepare(sessionUuid: string) {
        return { payload: { sessionUuid } };
      },
    },
    sessionsRevokeOthers(state) {
      state.sessionsInProcess = true;
    },
    sessionsLoadResponse: {
      reducer(state, action: { payload: { sessions: IAdminSessionEntry[] } }) {
        state.sessions = action.payload.sessions;
        state.sessionsInProcess = false;
      },
      prepare(sessions: IAdminSessionEntry[]) {
        return { payload: { sessions } };
      },
    },
    logout(state) {
      state.email = undefined;
      state.uuid = undefined;
      state.username = undefined;
      state.isLoggedIn = false;
      finishLoading(state);
    },
    logoutResponse(state) {
      finishLoading(state);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(requestFailedMatcher('User'), (state) => {
      resetLoadingOnRequestFailed(state);
      state.authLogInProcess = false;
      state.sessionsInProcess = false;
      state.profileInProcess = false;
    });
  },
});

export const {
  login,
  loginResponse,
  loginReset,
  changePassword,
  changePasswordResponse,
  usernameChange,
  usernameChangeResponse,
  profileLoad,
  profileLoadResponse,
  authLogLoad,
  authLogLoadResponse,
  sessionsLoad,
  sessionRevoke,
  sessionsRevokeOthers,
  sessionsLoadResponse,
  logout,
  logoutResponse,
} = userSlice.actions;
export default userSlice.reducer;
